import { useRef, useEffect } from "react";
import { createRoot } from 'react-dom/client';

// Google's AdvancedMarkerElement throws InvalidValueError ("not a number") if
// position.lat / position.lng aren't finite numbers — and one bad marker takes
// down the whole map. Some comps (e.g. excluded taxonomy branches) carry
// missing / un-geocoded addressDetails, so guard every position we hand off.
const isValidPosition = (pos) =>
    pos != null && Number.isFinite(pos.lat) && Number.isFinite(pos.lng);

const Marker = (props) => {
    const map = props.map;
    const position = props.position;
    const children = props.children;
    const onClick = props.onClick;
    const zIndex = props.zIndex;

    const rootRef = useRef(null);
    const markerRef = useRef(null);
    const listenerRef = useRef(null);

    // Keep the latest onClick in a ref so the single click listener (attached
    // once, below) always invokes the current handler. Required because some
    // callers (CompsMarkersLayer) pass a fresh inline onClick every render; we
    // must not re-add the listener per render, and must not call a stale one.
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;

    // Create the React root + AdvancedMarkerElement EXACTLY ONCE, when the map
    // is available, and tear them down only on true component unmount. Keying
    // this effect to [map] (not [map, position, zIndex]) is what stops the
    // per-render detach/reattach churn: position/zIndex/children sync below
    // without ever pulling the overlay off the map or re-adding the listener.
    useEffect(() => {
        if (!window.google || !map) return undefined;

        if (!rootRef.current) {
            const container = document.createElement("div");
            rootRef.current = createRoot(container);

            const marker = new window.google.maps.marker.AdvancedMarkerElement({
                // Only seed position when valid — an invalid lat/lng here throws
                // in the constructor. The sync effect below sets it once the
                // coords become valid (if ever).
                ...(isValidPosition(position) ? { position } : {}),
                content: container,
                zIndex: zIndex || 1,
            });
            markerRef.current = marker;
            // Attach once here so it isn't torn off / re-added on every render.
            marker.map = map;
            // Single click listener; delegates to the latest onClick via ref.
            listenerRef.current = marker.addListener("click", (e) => {
                if (onClickRef.current) onClickRef.current(e);
            });
        }

        return () => {
            if (listenerRef.current) {
                listenerRef.current.remove();
                listenerRef.current = null;
            }
            if (markerRef.current) {
                markerRef.current.map = null;
                markerRef.current = null;
            }
            // Unmount the React root asynchronously: calling root.unmount()
            // synchronously inside this cleanup (which can run during a React
            // commit) throws "Attempted to synchronously unmount a root while
            // React was already rendering". Capture + null the ref first so a
            // StrictMode remount can't double-unmount the same root.
            const root = rootRef.current;
            rootRef.current = null;
            if (root) queueMicrotask(() => root.unmount());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);

    // Sync the cheap, frequently-changing props on every render WITHOUT
    // detaching the marker from the map or re-adding listeners. This preserves
    // hover/click bubble recolors (children re-render), repositioning, and
    // zIndex changes for all callers.
    useEffect(() => {
        if (!rootRef.current || !markerRef.current) return;
        rootRef.current.render(children);
        // Skip invalid coords — assigning a non-finite lat/lng throws and breaks
        // the whole map. Leave the marker at its last valid position instead.
        if (isValidPosition(position)) markerRef.current.position = position;
        markerRef.current.zIndex = zIndex || 1;
    }, [children, position, zIndex]);

    return null;
};

export default Marker;
